/**
 * CompareVs — `/vs/:competitor` comparison pages.
 *
 * Six high-intent comparison pages built from one config-driven component.
 * Each page targets buyers who are explicitly searching for an alternative
 * to one of our biggest competitors in Canada (Buildium, AppFolio,
 * Rentsync, Yardi Breeze, TenantCloud, Avail).
 *
 * SEO strategy:
 *   • One page per competitor — slug = vendor brand name.
 *   • H1 includes "[Competitor] alternative for Canadian landlords" so the
 *     page can rank for the exact buyer-intent query.
 *   • JSON-LD: SoftwareApplication + FAQPage + a comparison ItemList that
 *     Google can render in SERP "alternatives" carousels.
 *   • Canonical = self (each /vs/:competitor is its own indexable page).
 *   • Internal-linked from the homepage footer + sitemap (auto).
 *
 * Design contract:
 *   • Public header so internal-link juice flows.
 *   • Side-by-side feature table (us vs them) is the only above-fold focus.
 *   • Embedded magic-link form for instant conversion.
 *   • FAQ section ALWAYS includes "Is Central Rentals cheaper than X?" and
 *     "Does Central Rentals support T776 / NR4?" — high-intent queries
 *     from Google Search Console that competitors don't cover.
 */
import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2, XCircle, MinusCircle, ArrowRight, Sparkles, Shield, MapPin, Star } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import HeroEmailStart from "@/components/landing/HeroEmailStart";
import { usePersonalization } from "@/lib/personalizer";

/* eslint-disable react/no-unescaped-entities */

// Canonical comparison rows used across every /vs page. Edit here to
// keep all comparison pages in lock-step — the dimensions never change,
// only the per-competitor verdicts do.
const ROWS = [
  { key: "country",        label: "Built for Canada" },
  { key: "leases",         label: "Provincial lease generator (all 13)" },
  { key: "notices",        label: "Provincial notices (N1-N13, Form A, RTDRS, TAL)" },
  { key: "stripeCAD",      label: "Rent tracking in CAD (peer-to-peer)" },
  { key: "t776",           label: "CRA T776 + NR4 + T5013 exports" },
  { key: "screening",      label: "Tenant screening (credit + ID + landlord)" },
  { key: "creditBuilder",  label: "Rent reported to Equifax + TransUnion" },
  { key: "smartAI",        label: "Smart assistant (Mark) — leases, listings, replies" },
  { key: "maintenance",    label: "Maintenance triage + work-order routing" },
  { key: "freeTrial",      label: "Starts at CA$35/month · Cancel anytime" },
  { key: "pricing",        label: "Starting price (per unit / month, CAD)" },
];

const CR = {
  country:        { v: true,  note: "Canadian-incorporated" },
  leases:         { v: true,  note: "All 13 provinces + Quebec TAL" },
  notices:        { v: true,  note: "Every provincial form" },
  stripeCAD:      { v: true,  note: "Native CAD + auto-reconciliation" },
  t776:           { v: true,  note: "1-click export at tax time" },
  screening:      { v: true,  note: "90-second 3-check pull" },
  creditBuilder:  { v: true,  note: "Free for tenants" },
  smartAI:        { v: true,  note: "Built-in" },
  maintenance:    { v: true,  note: "Auto-triaged + vendor routing" },
  freeTrial:      { v: true,  note: "From CA$35/mo, cancel anytime" },
  pricing:        { v: "$35/mo", note: "CAD / unit / month" },
};

const COMPETITORS = {
  buildium: {
    slug: "buildium",
    name: "Buildium",
    title: "Buildium alternative for Canadian landlords — Central Rentals",
    desc: "Looking for a Buildium alternative built for Canadian landlords? Central Rentals handles all 13 provinces, T776 exports, and CAD-native Stripe — at a fraction of Buildium's price.",
    h1: "The Buildium alternative built for Canada.",
    intro: "Buildium is a powerful US-first PMS, but Canadian landlords often hit walls: no provincial notice library, no CRA exports, USD-first billing, and pricing that scales aggressively past 10 units. Central Rentals is built in Canada, by Canadian landlords, for Canadian landlords.",
    them: {
      country:       { v: "partial", note: "US-first; CAD is an add-on" },
      leases:        { v: false,     note: "US lease templates only" },
      notices:       { v: false,     note: "No provincial notices" },
      stripeCAD:     { v: "partial", note: "USD-first; CAD via workaround" },
      t776:          { v: false,     note: "US tax forms (1099)" },
      screening:     { v: true,      note: "Via TransUnion partner" },
      creditBuilder: { v: false,     note: "Not available in Canada" },
      smartAI:       { v: "partial", note: "Basic add-on, US$/mo extra" },
      maintenance:   { v: true,      note: "Robust, but US-focused vendors" },
      freeTrial:     { v: "partial", note: "14 days, credit card required" },
      pricing:       { v: "$58 USD", note: "Minimum + per-unit fees" },
    },
    bottomLine: "If you're a Canadian landlord with under 50 units, you're paying for Buildium features you can't use. Central Rentals gives you the Canadian-specific features Buildium doesn't, for ~25× less.",
  },
  appfolio: {
    slug: "appfolio",
    name: "AppFolio",
    title: "AppFolio alternative for Canadian landlords — Central Rentals",
    desc: "AppFolio is built for 50+ unit US property managers. Central Rentals is built for Canadian landlords of any size, with CAD-native Stripe and CRA exports.",
    h1: "The AppFolio alternative for Canadian landlords.",
    intro: "AppFolio's minimum monthly fee starts at US$298 — and you need at least 50 units to make it worthwhile. Central Rentals has no minimum, scales from 1 to 1,000+ units, and was purpose-built around Canadian provincial rental law.",
    them: {
      country:       { v: false,     note: "US only" },
      leases:        { v: false,     note: "US lease templates" },
      notices:       { v: false,     note: "No provincial notices" },
      stripeCAD:     { v: false,     note: "USD only via ACH" },
      t776:          { v: false,     note: "1099 only" },
      screening:     { v: true,      note: "Via Experian (US)" },
      creditBuilder: { v: false,     note: "Not in Canada" },
      smartAI:       { v: true,      note: "AppFolio Leasing Agent (US$/mo extra)" },
      maintenance:   { v: true,      note: "Excellent, US vendor network" },
      freeTrial:     { v: false,     note: "Demo only — no self-serve" },
      pricing:       { v: "$298+ USD", note: "Min monthly + per-unit" },
    },
    bottomLine: "AppFolio is overkill for Canadian landlords under 50 units and underkill on Canadian compliance for any landlord. Central Rentals gives you both — Canadian rental law + small-portfolio pricing.",
  },
  rentsync: {
    slug: "rentsync",
    name: "Rentsync",
    title: "Rentsync alternative for Canadian landlords — Central Rentals",
    desc: "Rentsync is a Canadian marketing/syndication platform — but it's not a full PMS. Central Rentals does syndication AND lease, rent, maintenance, tax in one place.",
    h1: "The Rentsync alternative that's a full property OS.",
    intro: "Rentsync is great at syndicating your listings to 60+ rental sites — but it's not built for collecting rent, generating leases, or doing your T776. You end up duct-taping it to a separate accounting and lease tool. Central Rentals does syndication + the rest in one platform.",
    them: {
      country:       { v: true,      note: "Canadian-built" },
      leases:        { v: false,     note: "Not a PMS — marketing only" },
      notices:       { v: false,     note: "Not a PMS" },
      stripeCAD:     { v: false,     note: "No rent collection" },
      t776:          { v: false,     note: "No accounting features" },
      screening:     { v: "partial", note: "Via Equifax integration" },
      creditBuilder: { v: false,     note: "Not available" },
      smartAI:       { v: "partial", note: "Smart listing copy only" },
      maintenance:   { v: false,     note: "Not a PMS" },
      freeTrial:     { v: false,     note: "Quote-based pricing" },
      pricing:       { v: "Custom",  note: "Sales-driven, $$$/mo" },
    },
    bottomLine: "Rentsync is a marketing channel; Central Rentals is the full operating system. Most landlords replace Rentsync + 2-3 other tools when they switch.",
  },
  "yardi-breeze": {
    slug: "yardi-breeze",
    name: "Yardi Breeze",
    title: "Yardi Breeze alternative for Canadian landlords — Central Rentals",
    desc: "Yardi Breeze is enterprise-grade software wrapped in a friendlier UI. Central Rentals is built specifically for owner-operators and small portfolios in Canada.",
    h1: "The Yardi Breeze alternative for small Canadian landlords.",
    intro: "Yardi Breeze starts at US$1/unit/month with a $100 monthly minimum, and the UX still feels like a stripped-down enterprise tool. Central Rentals was designed from scratch for the 1-50-unit Canadian landlord, with the workflows you actually use.",
    them: {
      country:       { v: "partial", note: "Available in Canada, US-first features" },
      leases:        { v: "partial", note: "Generic templates; not provincial" },
      notices:       { v: false,     note: "No provincial notice library" },
      stripeCAD:     { v: "partial", note: "Yardi payment add-on (extra fee)" },
      t776:          { v: false,     note: "US 1099 only" },
      screening:     { v: true,      note: "Via Yardi Resident Screening" },
      creditBuilder: { v: false,     note: "Not available" },
      smartAI:       { v: false,     note: "Not included" },
      maintenance:   { v: true,      note: "Solid, but enterprise-flavoured" },
      freeTrial:     { v: false,     note: "Demo + sales call required" },
      pricing:       { v: "$100+ USD", note: "Min + per-unit + add-ons" },
    },
    bottomLine: "Yardi Breeze is great if you've got 100+ units and need enterprise compliance. For 1-50 units in Canada, Central Rentals is faster to set up, cheaper, and built around your provincial rules.",
  },
  tenantcloud: {
    slug: "tenantcloud",
    name: "TenantCloud",
    title: "TenantCloud alternative for Canadian landlords — Central Rentals",
    desc: "TenantCloud has a free tier but limited Canadian compliance. Central Rentals gives you full Canadian provincial workflows and a paid plans from CA$35/month.",
    h1: "The TenantCloud alternative for Canadian rental law.",
    intro: "TenantCloud is a popular freemium tool — but the free tier is missing rent collection, the paid tiers don't ship Canadian provincial notices, and there's no T776 export. Central Rentals gives you a full-featured Paid plan, cancel any time, and every Canadian feature on day one.",
    them: {
      country:       { v: false,     note: "US-first" },
      leases:        { v: "partial", note: "Generic templates; users edit manually" },
      notices:       { v: false,     note: "No provincial notice library" },
      stripeCAD:     { v: "partial", note: "Stripe + ACH, USD-default" },
      t776:          { v: false,     note: "Schedule E only (US)" },
      screening:     { v: true,      note: "TransUnion (US)" },
      creditBuilder: { v: false,     note: "Not available in Canada" },
      smartAI:       { v: false,     note: "Not included" },
      maintenance:   { v: true,      note: "Vendor portal works in Canada" },
      freeTrial:     { v: "partial", note: "Free tier exists but features capped" },
      pricing:       { v: "$15 USD", note: "Per landlord/mo (Growth plan)" },
    },
    bottomLine: "TenantCloud is fine if you're in the US and don't want to pay. For Canadian compliance — provincial notices, T776, CAD-first Stripe — Central Rentals is built for you.",
  },
  avail: {
    slug: "avail",
    name: "Avail",
    title: "Avail alternative for Canadian landlords — Central Rentals",
    desc: "Avail (now Realtor.com Avail) is built for US DIY landlords. Central Rentals is the Canadian equivalent — built around CRA exports, provincial leases, and CAD-native Stripe.",
    h1: "The Avail alternative for Canadian DIY landlords.",
    intro: "Avail is a clean DIY tool for US landlords with 1-10 units — but it doesn't support Canadian provincial leases, doesn't generate CRA T776, and the credit reporting is US-only. Central Rentals is what Canadian DIY landlords actually need.",
    them: {
      country:       { v: false,     note: "US only" },
      leases:        { v: false,     note: "US state leases" },
      notices:       { v: false,     note: "No provincial notices" },
      stripeCAD:     { v: false,     note: "USD only" },
      t776:          { v: false,     note: "US 1099 only" },
      screening:     { v: true,      note: "TransUnion (US)" },
      creditBuilder: { v: true,      note: "US-only reporting" },
      smartAI:       { v: false,     note: "Not available" },
      maintenance:   { v: "partial", note: "Basic maintenance requests" },
      freeTrial:     { v: true,      note: "Free tier" },
      pricing:       { v: "$0-$9 USD", note: "Per unit + listing fees" },
    },
    bottomLine: "Avail is great if you're a US landlord. Central Rentals is the Canadian version — same simplicity, same DIY ethos, but with the provincial law and CRA features Avail can't give you.",
  },
  "dwell-property-management": {
    slug: "dwell-property-management",
    name: "Dwell Property Management",
    title: "Dwell Property Management alternative — Central Rentals Canada",
    desc: "Dwell Property Management is a boutique property-management SERVICE (a manager takes 8-12% of your rent). Central Rentals is a self-serve SOFTWARE platform — a flat monthly fee, no percentage skim, and you keep control.",
    h1: "The Dwell Property Management alternative — keep your rent, use software instead.",
    intro: "Dwell is a full-service property manager: you hand over the keys and they take 8-12% of every rent cheque for the life of the tenancy. On a $2,500/mo Vancouver unit that's $2,400-$3,600 a year, forever. Central Rentals is a flat-priced software platform that gives you the same automation Dwell uses internally — rent collection, tenant screening, maintenance triage, LTB/RTB notice filing — for a flat $49/mo. Keep 100% of your rent and only pay for the software.",
    them: {
      country:       { v: true,      note: "Canadian (Vancouver/Toronto)" },
      leases:        { v: true,      note: "Manager handles — you can't audit" },
      notices:       { v: true,      note: "Manager files — extra fee for LTB" },
      stripeCAD:     { v: true,      note: "You never see the rent flow" },
      t776:          { v: "partial", note: "Year-end statement — but not turnkey" },
      screening:     { v: true,      note: "Manager decides — you can't override" },
      creditBuilder: { v: false,     note: "Not offered" },
      smartAI:       { v: false,     note: "Human-only — no smart drafting" },
      maintenance:   { v: true,      note: "Managed — plus 15% vendor markup" },
      freeTrial:     { v: false,     note: "Contract-based" },
      pricing:       { v: "8-12% of rent", note: "Plus setup + leasing fees" },
    },
    bottomLine: "Dwell is worth the 8-12% skim if you're an absentee landlord who never wants to touch tenant messages. But if you'd rather keep your rent and use software that does 90% of what a property manager does automatically — Central Rentals is that software, at a flat $49/mo. On a 4-unit portfolio you save ~$8,000 a year and keep control.",
  },
  // ── Feb 2026 additions — Canadian-first competitors currently ranking
  // in Google AI Overviews. Each page is designed to rank for the
  // "[Competitor] alternative Canada" query.
  "pendo-rentals": {
    slug: "pendo-rentals",
    name: "Pendo Rentals",
    title: "Pendo Rentals alternative for Canadian landlords — Central Rentals",
    desc: "Pendo Rentals starts at CA$15/month. Central Rentals starts at CA$35/month with T776 exports, French Québec UI, and all 13 provinces covered. See the side-by-side.",
    h1: "The Pendo Rentals alternative — 3× cheaper, all 13 provinces.",
    intro: "Pendo Rentals is a solid Canadian-built platform for small landlords, but at CA$15/month it's 3× the price of Central Rentals, has no French Québec UI, and its notice library is Ontario-heavy. Central Rentals covers every province and territory with lease + notice templates in both English and French, at CA$35/month.",
    them: {
      country:       { v: true,      note: "Canadian" },
      leases:        { v: "partial", note: "Ontario-first, sparse in QC/AB/BC" },
      notices:       { v: "partial", note: "LTB forms only" },
      stripeCAD:     { v: true,      note: "PendoPay (proprietary rails)" },
      t776:          { v: false,     note: "No T776 export" },
      screening:     { v: true,      note: "Via 3rd-party bureau" },
      creditBuilder: { v: false,     note: "Not offered" },
      smartAI:       { v: false,     note: "No smart assistant" },
      maintenance:   { v: true,      note: "Basic ticket log" },
      freeTrial:     { v: "partial", note: "30-day trial, card required" },
      pricing:       { v: "CA$15/mo", note: "3× Central Rentals" },
    },
    bottomLine: "Pendo is fine for Ontario landlords with 1–5 doors who don't need French. If you're anywhere else in Canada, or you want CRA exports at tax time, Central Rentals is the better fit at 1/3 the price.",
  },
  "mi-property-portal": {
    slug: "mi-property-portal",
    name: "Mi Property Portal",
    title: "Mi Property Portal alternative for Canadian landlords — Central Rentals",
    desc: "Mi Property Portal is Ontario-focused and priced per-unit. Central Rentals covers all 13 provinces flat-rate from CA$35/month with T776 exports and French UI.",
    h1: "The Mi Property Portal alternative — every province, not just Ontario.",
    intro: "Mi Property Portal built its business around Ontario LTB workflows — which is great if you're in Toronto but limits you the moment you buy a door in Vancouver, Calgary or Montréal. Central Rentals ships lease + notice templates for every province and territory and speaks French for Québec tenants.",
    them: {
      country:       { v: true,      note: "Canadian (Ontario)" },
      leases:        { v: "partial", note: "Ontario Standard Lease only" },
      notices:       { v: "partial", note: "LTB N1-N13 only" },
      stripeCAD:     { v: true,      note: "EFT + PAD" },
      t776:          { v: false,     note: "No native T776 export" },
      screening:     { v: true,      note: "Via Certn" },
      creditBuilder: { v: false,     note: "Not offered" },
      smartAI:       { v: false,     note: "No smart assistant" },
      maintenance:   { v: true,      note: "Manual assignment" },
      freeTrial:     { v: "partial", note: "Demo required" },
      pricing:       { v: "~CA$20/mo", note: "Per-unit fees stack fast" },
    },
    bottomLine: "If you own only in Ontario and only need LTB forms, Mi Property Portal works. For a national portfolio, Québec French, or CRA T776 exports at tax time, Central Rentals is a materially better fit.",
  },
  "building-stack": {
    slug: "building-stack",
    name: "Building Stack",
    title: "Building Stack alternative for Canadian landlords — Central Rentals",
    desc: "Building Stack is Montréal-built for multi-family complexes. Central Rentals scales from 1 door to 500 with CAD-native rent, CRA exports, and every provincial lease.",
    h1: "The Building Stack alternative — from 1 door to 500, still Canadian.",
    intro: "Building Stack targets 50+ unit multi-family operators in Québec and Ontario. If you have a triplex or single-family portfolio you'll pay for enterprise features you never use. Central Rentals has no minimum door count, flat-rate pricing from CA$35/month, and full French Québec + English support across every province.",
    them: {
      country:       { v: true,      note: "Canadian (Montréal)" },
      leases:        { v: true,      note: "Bilingual (QC + ON focus)" },
      notices:       { v: "partial", note: "TAL + LTB only" },
      stripeCAD:     { v: true,      note: "Native CAD" },
      t776:          { v: false,     note: "No T776 export" },
      screening:     { v: true,      note: "Certn integration" },
      creditBuilder: { v: false,     note: "Not offered" },
      smartAI:       { v: false,     note: "No smart assistant" },
      maintenance:   { v: true,      note: "Full work-order routing" },
      freeTrial:     { v: false,     note: "Sales-led demo only" },
      pricing:       { v: "~CA$35/mo", note: "Plus per-door fees" },
    },
    bottomLine: "Building Stack is excellent for 50+ door multi-family in Québec / Ontario. For anything smaller, or portfolios outside QC/ON, Central Rentals delivers the same functionality without the enterprise overhead — at 1/7 the price.",
  },
  "doorloop": {
    slug: "doorloop",
    name: "DoorLoop",
    title: "DoorLoop alternative for Canadian landlords — Central Rentals",
    desc: "DoorLoop is US-built and priced in USD (~$69/mo). Central Rentals is Canadian-built with CAD pricing from CA$35/month, T776/NR4 exports, and provincial leases.",
    h1: "The DoorLoop alternative — Canadian by design, from CA$35/mo.",
    intro: "DoorLoop is a great US-first platform, but Canadian landlords pay in USD, get US lease templates, and have no CRA T776/NR4 tax exports. Central Rentals is built for Canadian landlords from day one — CAD pricing, provincial leases in all 13 jurisdictions, French UI, and 1-click CRA exports at tax time.",
    them: {
      country:       { v: false,     note: "US-based" },
      leases:        { v: false,     note: "US templates" },
      notices:       { v: false,     note: "US eviction only" },
      stripeCAD:     { v: "partial", note: "USD default, CAD add-on" },
      t776:          { v: false,     note: "No T776" },
      screening:     { v: true,      note: "TransUnion (US)" },
      creditBuilder: { v: false,     note: "Not offered in Canada" },
      smartAI:       { v: true,      note: "AI assistant" },
      maintenance:   { v: true,      note: "Full ticketing" },
      freeTrial:     { v: false,     note: "Demo required" },
      pricing:       { v: "US$69/mo", note: "~CA$95/mo after FX" },
    },
    bottomLine: "DoorLoop is polished but priced and templated for the US market. Canadian landlords pay ~14× more than Central Rentals for a platform that still requires manual work at tax time.",
  },
  "turbotenant": {
    slug: "turbotenant",
    name: "TurboTenant",
    title: "TurboTenant alternative for Canadian landlords — Central Rentals",
    desc: "TurboTenant is free but US-only. Central Rentals is the Canadian equivalent with CAD rent, provincial leases, and T776 exports from CA$35/month.",
    h1: "The TurboTenant alternative — for actual Canadian landlords.",
    intro: "TurboTenant's free tier is attractive, but Canadian landlords quickly hit the walls: no CAD payments, no provincial lease templates, no LTB / RTB / TAL notices, no T776 export. Central Rentals is the Canadian version — same DIY simplicity, same low price point, but every screen was built for Canadian rental law.",
    them: {
      country:       { v: false,     note: "US-based" },
      leases:        { v: false,     note: "US state templates" },
      notices:       { v: false,     note: "US eviction only" },
      stripeCAD:     { v: false,     note: "USD only" },
      t776:          { v: false,     note: "No CRA exports" },
      screening:     { v: true,      note: "TransUnion (US)" },
      creditBuilder: { v: false,     note: "Not for Canadian tenants" },
      smartAI:       { v: "partial", note: "US-context only" },
      maintenance:   { v: true,      note: "Basic tickets" },
      freeTrial:     { v: true,      note: "Free tier available" },
      pricing:       { v: "US$0-$9", note: "Free but US-only" },
    },
    bottomLine: "TurboTenant is free — but useless for a Canadian landlord because it can't collect CAD rent, doesn't have your provincial lease, and skips T776 at tax time. Central Rentals costs $35/mo and actually works in Canada.",
  },
  "rentals-ca": {
    slug: "rentals-ca",
    name: "Rentals.ca",
    title: "Rentals.ca alternative for Canadian landlords — Central Rentals",
    desc: "Rentals.ca is a Canadian listing marketplace, not a full property management platform. Central Rentals syndicates listings AND collects rent, generates provincial notices, screens tenants and exports your T776 — all in one place.",
    h1: "The Rentals.ca alternative that runs the whole rental.",
    intro: "Rentals.ca is a great place to advertise a vacancy in Canada — but the second the tenant moves in, you're back to Excel, Interac e-Transfer, and a folder of PDF lease templates. Central Rentals covers the whole lifecycle: list the unit, screen the applicant, sign the provincial lease, collect the rent, log maintenance, generate N4/N12/TAL notices, and export a CRA-ready T776 at year end.",
    them: {
      country:       { v: true,      note: "Canadian-built listing site" },
      leases:        { v: false,     note: "Not a PMS — listings only" },
      notices:       { v: false,     note: "No notice generator" },
      stripeCAD:     { v: false,     note: "No rent collection" },
      t776:          { v: false,     note: "No accounting" },
      screening:     { v: "partial", note: "Basic pre-screen questions" },
      creditBuilder: { v: false,     note: "Not offered" },
      smartAI:       { v: "partial", note: "Rent-price suggestion only" },
      maintenance:   { v: false,     note: "Not a PMS" },
      freeTrial:     { v: "partial", note: "Free posts + paid featured" },
      pricing:       { v: "$0-$100+", note: "Per-listing fees" },
    },
    bottomLine: "Rentals.ca ends the day the tenant signs. Central Rentals starts there — and takes you all the way to a CRA-ready tax export next April.",
  },
  "realtor-ca": {
    slug: "realtor-ca",
    name: "Realtor.ca",
    title: "Realtor.ca alternative for Canadian landlords managing rentals — Central Rentals",
    desc: "Realtor.ca (CREA's MLS site) is designed for buying and selling — not managing rentals. Central Rentals is the property management platform Canadian landlords use once the unit is bought and rented out.",
    h1: "Realtor.ca lists it. Central Rentals runs it.",
    intro: "Realtor.ca (powered by CREA's MLS® system) is Canada's dominant search engine for buying and selling real estate — but it's not a property management platform. Once the unit is rented, landlords need software that collects CAD rent, generates provincial notices, tracks maintenance, and files their T776 at year end. That's what Central Rentals is for.",
    them: {
      country:       { v: true,      note: "Canadian, CREA-run" },
      leases:        { v: false,     note: "Buy/sell — not rentals" },
      notices:       { v: false,     note: "Not a PMS" },
      stripeCAD:     { v: false,     note: "No rent collection" },
      t776:          { v: false,     note: "No landlord accounting" },
      screening:     { v: false,     note: "Not offered" },
      creditBuilder: { v: false,     note: "Not applicable" },
      smartAI:       { v: "partial", note: "Search + valuation only" },
      maintenance:   { v: false,     note: "Not a PMS" },
      freeTrial:     { v: true,      note: "Free public search" },
      pricing:       { v: "Free",     note: "Buyer/seller-facing" },
    },
    bottomLine: "Realtor.ca is where Canadian landlords buy the unit. Central Rentals is what Canadian landlords use to make money on the unit for the next 20 years.",
  },
  "apartments-com": {
    slug: "apartments-com",
    name: "Apartments.com",
    title: "Apartments.com alternative for Canadian landlords — Central Rentals",
    desc: "Apartments.com is a US-first marketplace with limited Canadian coverage. Central Rentals is the Canadian-built property management platform that handles CAD rent, provincial leases, and CRA exports.",
    h1: "The Apartments.com alternative built for Canada.",
    intro: "Apartments.com dominates US rental search, but its Canadian coverage is patchy and its landlord tools (rent collection, lease templates, notice generation) are US-only. Central Rentals is the Canadian equivalent: syndication into 20+ Canadian rental sites, plus everything the tenant experience needs after they move in.",
    them: {
      country:       { v: false,     note: "US-first, thin Canadian coverage" },
      leases:        { v: false,     note: "US state templates" },
      notices:       { v: false,     note: "US eviction forms only" },
      stripeCAD:     { v: false,     note: "USD-only" },
      t776:          { v: false,     note: "1099 only" },
      screening:     { v: true,      note: "Via TransUnion (US)" },
      creditBuilder: { v: false,     note: "Not for Canadian tenants" },
      smartAI:       { v: "partial", note: "Listing description tools" },
      maintenance:   { v: true,      note: "US-focused vendor network" },
      freeTrial:     { v: true,      note: "Free listing + paid boosts" },
      pricing:       { v: "US$0-$70", note: "USD-billed even in Canada" },
    },
    bottomLine: "Apartments.com is fine if you rent in Buffalo. If you rent in Brantford, you need Central Rentals — CAD payments, provincial leases, and a CRA T776 export that actually files.",
  },
};

export const COMPETITOR_SLUGS = Object.keys(COMPETITORS);

function VerdictIcon({ value }) {
  if (value === true) return <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-none"/>;
  if (value === false) return <XCircle className="w-5 h-5 text-rose-500 flex-none"/>;
  if (value === "partial") return <MinusCircle className="w-5 h-5 text-amber-500 flex-none"/>;
  // Custom string (price or other) — render the text directly
  return <span className="font-bold text-slate-900 text-sm tabular-nums">{value}</span>;
}

function setMetaTag(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement(sel.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

export default function CompareVs() {
  const { competitor } = useParams();
  const cfg = COMPETITORS[competitor];
  const personal = usePersonalization();

  useEffect(() => {
    if (!cfg) return;
    const prevTitle = document.title;
    document.title = cfg.title;
    const url = `https://centralrentals.ca/vs/${cfg.slug}`;
    const tags = [
      setMetaTag('meta[name="description"]', { name: "description", content: cfg.desc }),
      setMetaTag('link[rel="canonical"]', { rel: "canonical", href: url }),
      setMetaTag('meta[property="og:type"]', { property: "og:type", content: "website" }),
      setMetaTag('meta[property="og:title"]', { property: "og:title", content: cfg.title }),
      setMetaTag('meta[property="og:description"]', { property: "og:description", content: cfg.desc }),
      setMetaTag('meta[property="og:url"]', { property: "og:url", content: url }),
      setMetaTag('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" }),
    ];

    // FAQs auto-generated from the comparison rows for FAQPage rich snippet.
    const faqs = [
      {
        q: `Is Central Rentals cheaper than ${cfg.name}?`,
        a: `Yes. Central Rentals starts at CA$35 per month (DIY / 1–10 units) with a paid plans from CA$35/month. ${cfg.name} ${cfg.them.pricing.v ? `starts at ${cfg.them.pricing.v}` : "uses sales-driven pricing"} — typically 25-300× more for small Canadian portfolios.`,
      },
      {
        q: `Does ${cfg.name} support Canadian provincial leases?`,
        a: `${cfg.them.leases.v === true ? "Yes, but generically — " : "No — "}${cfg.them.leases.note}. Central Rentals ships native provincial lease generators for all 13 provinces and territories, including the Quebec TAL and the Ontario Standard Lease (Form 2229E).`,
      },
      {
        q: `Can I export to T776 / NR4 / T5013 with ${cfg.name}?`,
        a: `${cfg.them.t776.v === true ? "Yes." : "No — " + cfg.them.t776.note + "."} Central Rentals generates CRA-ready exports at tax time with one click.`,
      },
      {
        q: `How long does it take to switch from ${cfg.name} to Central Rentals?`,
        a: `Most landlords with under 25 units move in under 90 minutes. We import tenants + leases + recurring rent schedules directly from CSV exports. No long-term contract, cancel anytime.`,
      },
    ];

    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          "name": "Central Rentals — Canadian Property Management Software",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web, iOS, Android",
          "url": url,
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "CAD", "description": "Paid plans from CA$35/month" },
          "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "127" },
        },
        {
          "@type": "FAQPage",
          "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Compare", "item": "https://centralrentals.ca/vs" },
            { "@type": "ListItem", "position": 2, "name": `Central Rentals vs ${cfg.name}`, "item": url },
          ],
        },
      ],
    };
    let script = document.head.querySelector('script[data-vs-jsonld="1"]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-vs-jsonld", "1");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);

    return () => {
      document.title = prevTitle;
      tags.forEach((t) => t?.remove?.());
      script?.remove?.();
    };
  }, [cfg]);

  if (!cfg) return <Navigate to="/vs" replace />;

  // Auto-generated FAQs to render (same content as JSON-LD)
  const faqsToRender = [
    {
      q: `Is Central Rentals cheaper than ${cfg.name}?`,
      a: `Yes. Central Rentals starts at CA$35 per month (DIY / 1–10 units) with a paid plans from CA$35/month. ${cfg.name} ${cfg.them.pricing.v ? `starts at ${cfg.them.pricing.v}` : "uses sales-driven pricing"} — typically 25-300× more for small Canadian portfolios.`,
    },
    {
      q: `Does ${cfg.name} support Canadian provincial leases?`,
      a: `${cfg.them.leases.v === true ? "Yes, but generically — " : "No — "}${cfg.them.leases.note}. Central Rentals ships native provincial lease generators for all 13 provinces and territories, including the Quebec TAL and the Ontario Standard Lease (Form 2229E).`,
    },
    {
      q: `Can I export to T776 / NR4 / T5013 with ${cfg.name}?`,
      a: `${cfg.them.t776.v === true ? "Yes." : "No — " + cfg.them.t776.note + "."} Central Rentals generates CRA-ready exports at tax time with one click.`,
    },
    {
      q: `How long does it take to switch from ${cfg.name} to Central Rentals?`,
      a: `Most landlords with under 25 units move in under 90 minutes. We import tenants + leases + recurring rent schedules directly from CSV exports. No long-term contract, cancel anytime.`,
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid={`vs-${cfg.slug}`}>
      <PublicHeader/>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-10 md:pt-14 pb-10">
        <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold tracking-wide text-emerald-800">
          <MapPin className="w-3.5 h-3.5"/> Compare · Canadian property management
        </div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-tighter text-slate-900" data-testid={`vs-${cfg.slug}-h1`}>
          {cfg.h1}
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-3xl leading-relaxed" data-testid={`vs-${cfg.slug}-intro`}>
          {cfg.intro}
        </p>
        <div className="mt-7 max-w-xl" data-testid={`vs-${cfg.slug}-cta`}>
          <HeroEmailStart personal={personal}/>
        </div>
      </section>

      {/* Feature comparison table */}
      <section className="max-w-6xl mx-auto px-5 py-8">
        <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-5">Central Rentals vs {cfg.name} — feature by feature</h2>
        <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm bg-white">
          <table className="w-full text-sm" data-testid={`vs-${cfg.slug}-table`}>
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left py-3 px-4 font-bold text-slate-700">Feature</th>
                <th className="text-left py-3 px-4 font-bold text-emerald-700">Central Rentals</th>
                <th className="text-left py-3 px-4 font-bold text-slate-700">{cfg.name}</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => {
                const us = CR[row.key];
                const them = cfg.them[row.key];
                return (
                  <tr key={row.key} className="border-b border-slate-100 last:border-0">
                    <td className="py-3 px-4 font-semibold text-slate-800">{row.label}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <VerdictIcon value={us.v}/>
                        <span className="text-xs text-slate-600">{us.note}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <VerdictIcon value={them.v}/>
                        <span className="text-xs text-slate-600">{them.note}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-5 text-xs text-slate-500 flex items-center gap-4 flex-wrap">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/> Available</span>
          <span className="inline-flex items-center gap-1.5"><MinusCircle className="w-3.5 h-3.5 text-amber-500"/> Partial / limited</span>
          <span className="inline-flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5 text-rose-500"/> Not available</span>
        </div>
      </section>

      {/* Bottom line */}
      <section className="max-w-6xl mx-auto px-5 py-10">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-200 p-6 md:p-8">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-emerald-600 flex-none mt-1"/>
            <div>
              <div className="font-display text-xl md:text-2xl font-extrabold text-slate-900 mb-2">Bottom line</div>
              <p className="text-slate-700 text-[15px] leading-relaxed" data-testid={`vs-${cfg.slug}-bottom-line`}>{cfg.bottomLine}</p>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Link to="/signup" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[15px] shadow-lg shadow-emerald-500/30 transition-colors" data-testid={`vs-${cfg.slug}-signup`}>
                  Choose a plan <ArrowRight className="w-4 h-4"/>
                </Link>
                <Link to="/get-started" className="text-sm font-bold text-slate-700 hover:text-emerald-700 underline-offset-2 hover:underline">
                  Or book a 15-min walk-through →
                </Link>
              </div>
              <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
                <div className="flex items-center gap-1">
                  {[0,1,2,3,4].map((i) => <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400"/>)}
                  <span className="ml-1 font-bold text-slate-700">4.9/5</span>
                </div>
                <span className="inline-flex items-center gap-1"><Shield className="w-3.5 h-3.5"/> SOC2-aligned · Canadian-owned · Stripe-verified</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-6xl mx-auto px-5 py-10">
        <h2 className="font-display text-2xl font-extrabold text-slate-900 mb-6">Frequently asked questions</h2>
        <div className="space-y-4">
          {faqsToRender.map((f, i) => (
            <details key={i} className="rounded-xl border border-slate-200 p-5 group">
              <summary className="font-bold text-slate-900 cursor-pointer text-[15px] group-open:mb-3">{f.q}</summary>
              <p className="text-slate-700 leading-relaxed text-sm">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* See other comparisons */}
      <section className="max-w-6xl mx-auto px-5 py-10 border-t border-slate-100">
        <h2 className="font-display text-lg font-bold text-slate-700 mb-4">Compare Central Rentals to other tools</h2>
        <div className="flex flex-wrap gap-2" data-testid="vs-other-links">
          {COMPETITOR_SLUGS.filter((s) => s !== cfg.slug).map((s) => (
            <Link key={s} to={`/vs/${s}`} className="px-3.5 py-1.5 rounded-full bg-slate-100 hover:bg-emerald-100 hover:text-emerald-800 text-xs font-bold text-slate-700 transition-colors">
              vs {COMPETITORS[s].name}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

/** Hub page at `/vs` — lists all comparison pages. Indexable, simple. */
export function CompareHub() {
  useEffect(() => {
    document.title = "Compare Central Rentals to other property management software";
    const tags = [
      setMetaTag('meta[name="description"]', { name: "description", content: "Compare Central Rentals to Buildium, AppFolio, Yardi Breeze, Rentsync, TenantCloud, and Avail. Built for Canadian landlords." }),
      setMetaTag('link[rel="canonical"]', { rel: "canonical", href: "https://centralrentals.ca/vs" }),
    ];
    return () => tags.forEach((t) => t?.remove?.());
  }, []);
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader/>
      <section className="max-w-5xl mx-auto px-5 pt-12 pb-20">
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter text-slate-900">Compare property management software for Canadian landlords</h1>
        <p className="mt-5 text-lg text-slate-600 max-w-3xl">Honest side-by-side comparisons of Central Rentals vs the property management tools Canadian landlords ask us about most.</p>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vs-hub-grid">
          {COMPETITOR_SLUGS.map((s) => {
            const c = COMPETITORS[s];
            return (
              <Link key={s} to={`/vs/${s}`} className="block rounded-2xl border border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all p-5 group">
                <div className="text-[11px] font-bold tracking-widest text-emerald-700 uppercase">Compare</div>
                <div className="mt-1 font-display text-xl font-bold text-slate-900 group-hover:text-emerald-700">vs {c.name}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-emerald-700 text-xs font-bold">
                  See the comparison <ArrowRight className="w-3.5 h-3.5"/>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
